import { useRouter } from 'next/router';
import { Box } from '@zoopla-eng/zds-components/box';
import { Grid } from '@zoopla-eng/zds-components/grid';
import { useIsLoggedIn } from '@zoopla/redux-reducer-user';
import { ZooplaLogo } from '@zoopla/assets';
import {
    AnnouncementMedium,
    SaveMedium,
    UserMedium,
    HomeMedium
} from '@zoopla-eng/zds-components/icons';
import {
    ActionListItem,
    Container,
    Nav,
    LinkList,
    ActionList,
    SkipToContent,
    UpdateIndicator
} from './Header.styles';
import { Button } from '@zoopla-eng/zds-components/button';
import { Link, LinkWithIcon } from '@zoopla-eng/zds-components/link';
import Menu from './Menu';
import {
    getSigninUrl,
    Navigation,
    OptimiselyEnabledNavigation,
    trackAccount,
    trackNav
} from './data';
import { queries } from '@zoopla-eng/zds-components/themes';
import { useBreakpointListener } from '@zoopla/dom-util';
import { useSeenNotifications } from './useSeenNotifications';
import { useIsHomeOwner } from './graphql/useIsHomeOwner';
import {
    useLazyOptimizelyFeatureFlag,
    useOptimizelyFeatureFlag
} from '@zoopla/optimizely';
import { VisuallyHidden } from '@zoopla/shared/components';

const notificationExperiment = '_my-home-notification_page-visible';

export default function Header() {
    const { asPath } = useRouter();
    const isLoggedIn = useIsLoggedIn();
    const signinUrl = getSigninUrl(asPath);
    const [hasSeenNotifications, setHasSeenNotifications] =
        useSeenNotifications();
    const [featureLoading, notificationsEnabled] = useLazyOptimizelyFeatureFlag(
        notificationExperiment
    );
    const isSmallScreen = useBreakpointListener(queries.L[100]);

    const shouldCheckHomeOwnerStatus =
        !featureLoading && isLoggedIn && notificationsEnabled && isSmallScreen;
    const displayNotificationsIcon = useIsHomeOwner(
        !shouldCheckHomeOwnerStatus
    );

    const [isInstantValuationEnabled] = useOptimizelyFeatureFlag(
        '_home-values_instant-valuation-menu'
    );

    const navigationArray = isInstantValuationEnabled
        ? [...Navigation, ...OptimiselyEnabledNavigation]
        : Navigation;

    return (
        <Container>
            <SkipToContent>
                <Button
                    {...{ as: 'a', href: '#main-content' }}
                    category="primary"
                >
                    Skip to main content
                </Button>
            </SkipToContent>
            <Nav
                aria-label="main"
                data-testid={`header`}
                data-is-logged-in={isLoggedIn ? 'true' : undefined}
            >
                <Grid.Item area="headermiddle" bpL100={{ area: 'headerleft' }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bpL100={{ justifyContent: 'left' }}
                    >
                        <Box width="110px" height="20px">
                            <a
                                href="/"
                                aria-label="Zoopla's Homepage"
                                data-testid="zoopla-logo"
                            >
                                <ZooplaLogo />
                            </a>
                        </Box>
                    </Box>
                </Grid.Item>
                <Grid.Item area="headerleft" bpL100={{ area: 'headermiddle' }}>
                    <Box bpL100={{ display: 'none' }}>
                        <Menu isLoggedIn={isLoggedIn} signinUrl={signinUrl} />
                    </Box>
                    <Box
                        display="none"
                        bpL100={{
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        <LinkList>
                            {navigationArray.map(({ title, href }, idx) => (
                                <li
                                    key={href}
                                    data-testid={`header-menu${idx}`}
                                >
                                    <Link
                                        href={href}
                                        onClick={trackNav(title)}
                                        muted
                                        disableVisited
                                    >
                                        {title}
                                    </Link>
                                </li>
                            ))}
                        </LinkList>
                    </Box>
                </Grid.Item>
                <Grid.Item area="headerright">
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        bpL100={{ display: 'none' }}
                    >
                        <ActionList>
                            <>
                                <ActionListItem>
                                    <LinkWithIcon
                                        onClick={trackAccount('my home')}
                                        href="/my-home/"
                                        iconPrefix={
                                            <HomeMedium title="My Home" />
                                        }
                                        stacked
                                        muted
                                        disableVisited
                                    >
                                        My Home
                                    </LinkWithIcon>
                                </ActionListItem>
                                {displayNotificationsIcon && (
                                    <ActionListItem>
                                        <LinkWithIcon
                                            href="/myaccount/my-home/notifications/"
                                            iconPrefix={
                                                <AnnouncementMedium title="Notifications" />
                                            }
                                            onClick={() => {
                                                setHasSeenNotifications();
                                            }}
                                            stacked
                                            muted
                                            disableVisited
                                        >
                                            Updates
                                            {!hasSeenNotifications ? (
                                                <UpdateIndicator data-testid="header-notifications-new">
                                                    <VisuallyHidden>
                                                        You have new updates
                                                    </VisuallyHidden>
                                                </UpdateIndicator>
                                            ) : null}
                                        </LinkWithIcon>
                                    </ActionListItem>
                                )}
                                <ActionListItem>
                                    <LinkWithIcon
                                        onClick={trackAccount(
                                            'favourite properties'
                                        )}
                                        href="/myaccount/favourites/"
                                        iconPrefix={
                                            <SaveMedium title="Saved" />
                                        }
                                        stacked
                                        muted
                                        disableVisited
                                    >
                                        Saved
                                    </LinkWithIcon>
                                </ActionListItem>
                                {!isLoggedIn && (
                                    <ActionListItem>
                                        <LinkWithIcon
                                            onClick={trackAccount('sign in')}
                                            href={signinUrl}
                                            iconPrefix={
                                                <UserMedium title="Sign in" />
                                            }
                                            stacked
                                            muted
                                            disableVisited
                                        >
                                            Sign in
                                        </LinkWithIcon>
                                    </ActionListItem>
                                )}
                            </>
                        </ActionList>
                    </Box>
                    <Box
                        display="none"
                        justifyContent="flex-end"
                        bpL100={{ display: 'flex' }}
                    >
                        <LinkList>
                            <li>
                                <Link
                                    href="/my-home/"
                                    onClick={trackAccount('my home')}
                                    muted
                                    disableVisited
                                >
                                    My Home
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/myaccount/favourites/"
                                    onClick={trackAccount(
                                        'favourite properties'
                                    )}
                                    muted
                                    disableVisited
                                >
                                    Saved
                                </Link>
                            </li>
                            {isLoggedIn ? (
                                <li>
                                    <Link
                                        href="/my-zoopla/"
                                        onClick={trackAccount()}
                                        muted
                                        disableVisited
                                    >
                                        Account
                                    </Link>
                                </li>
                            ) : (
                                <li>
                                    <Link
                                        href={signinUrl}
                                        onClick={trackAccount('sign in')}
                                        muted
                                        disableVisited
                                    >
                                        Sign in
                                    </Link>
                                </li>
                            )}
                        </LinkList>
                    </Box>
                </Grid.Item>
            </Nav>
        </Container>
    );
}
